import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { getFolderSnapshot } from "@/lib/mail/inboxFolderBus";
import {
  filterMessagesLocalSearch,
  mergeSearchResultsByUid,
} from "@/lib/mail/localMessageSearch";
import type { MailMessageSummary } from "@/lib/types/mail";

type Options = {
  accountId?: number;
  folder: string;
  isOutboxView: boolean;
  searchInput: string;
  searchQuery: string;
  /** Requête IMAP à laquelle correspondent `serverMessages` (évite flash résultats périmés). */
  serverSearchQuery?: string;
  serverMessages: MailMessageSummary[];
  serverSyncing: boolean;
  applyLabels?: (messages: MailMessageSummary[]) => MailMessageSummary[];
};

export type MailSearchDisplay = {
  isSearchView: boolean;
  messages: MailMessageSummary[];
  /** Requête IMAP lancée et réponse pas encore reçue. */
  imapSearchLoading: boolean;
  /** Debounce en cours (frappe vs requête commitée). */
  searchDebouncePending: boolean;
  localMatchCount: number;
  committedQuery: string;
  activeQuery: string;
  /** Autorisé d’afficher « Aucun résultat » (IMAP terminé). */
  showEmptyResults: boolean;
};

/**
 * P0 recherche — couche d’affichage isolée du moteur liste (`useInboxMessages`).
 * Résultats locaux instantanés + fusion pendant l’IMAP, sans toucher au mode normal.
 */
export function useMailSearchDisplay({
  accountId,
  folder,
  isOutboxView,
  searchInput,
  searchQuery,
  serverSearchQuery = "",
  serverMessages,
  serverSyncing,
  applyLabels,
}: Options): MailSearchDisplay {
  const typingQuery = searchInput.trim();
  const committedQuery = searchQuery.trim();
  const activeQuery = typingQuery || committedQuery;
  const isSearchView = !isOutboxView && activeQuery.length > 0;

  const wasSearchActiveRef = useRef(false);
  const [localPool, setLocalPool] = useState<MailMessageSummary[]>([]);

  useLayoutEffect(() => {
    if (isOutboxView || accountId == null) {
      setLocalPool([]);
      wasSearchActiveRef.current = false;
      return;
    }

    const searchActive = typingQuery.length > 0 || committedQuery.length > 0;

    if (searchActive && !wasSearchActiveRef.current) {
      const snap = getFolderSnapshot(accountId, folder);
      setLocalPool(snap?.messages?.length ? [...snap.messages] : []);
    }

    if (!searchActive) {
      setLocalPool([]);
    }

    wasSearchActiveRef.current = searchActive;
  }, [accountId, committedQuery, folder, isOutboxView, typingQuery]);

  return useMemo(() => {
    if (!isSearchView) {
      return {
        isSearchView: false,
        messages: serverMessages,
        imapSearchLoading: false,
        searchDebouncePending: false,
        localMatchCount: 0,
        committedQuery,
        activeQuery,
        showEmptyResults: false,
      };
    }

    const pool = applyLabels ? applyLabels(localPool) : localPool;
    const localMatches = filterMessagesLocalSearch(pool, activeQuery);
    const searchDebouncePending =
      typingQuery.length > 0 && typingQuery !== committedQuery;
    const imapSearchLoading = committedQuery.length > 0 && serverSyncing;
    const serverStale =
      committedQuery.length > 0 &&
      serverSearchQuery.length > 0 &&
      serverSearchQuery !== committedQuery;
    const effectiveServer = serverStale ? [] : serverMessages;

    let messages: MailMessageSummary[];

    if (committedQuery.length === 0 || searchDebouncePending) {
      messages = localMatches;
    } else if (imapSearchLoading) {
      messages = mergeSearchResultsByUid(localMatches, effectiveServer);
    } else if (effectiveServer.length > 0) {
      messages = effectiveServer;
    } else {
      messages = localMatches;
    }

    const showEmptyResults =
      committedQuery.length > 0 &&
      !searchDebouncePending &&
      !imapSearchLoading &&
      messages.length === 0;

    return {
      isSearchView: true,
      messages,
      imapSearchLoading,
      searchDebouncePending,
      localMatchCount: localMatches.length,
      committedQuery,
      activeQuery,
      showEmptyResults,
    };
  }, [
    activeQuery,
    applyLabels,
    committedQuery,
    isSearchView,
    localPool,
    serverMessages,
    serverSearchQuery,
    serverSyncing,
    typingQuery,
  ]);
}
